import { useEffect, useMemo, useState } from "react"
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Segmented,
  Select,
  Switch,
  Table,
  Tooltip,
  message,
} from "antd"
import {
  DownloadOutlined,
  FilePdfOutlined,
  LeftOutlined,
  MailOutlined,
  RightOutlined,
} from "@ant-design/icons"
import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import dayjs from "dayjs"
import isoWeek from "dayjs/plugin/isoWeek"
import EmptyState from "../../components/EmptyState/EmptyState"
import apiClient from "../../Utils/apiClient"
import {
  formatDuration,
  formatHours,
  formatPercent,
  hoursFromSeconds,
} from "../../Utils/userActivity"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartTooltip,
  XAxis,
  YAxis,
} from "recharts"

dayjs.extend(isoWeek)

const { RangePicker } = DatePicker

const PERIOD_OPTIONS = [
  { label: "Daily", value: "day" },
  { label: "Weekly", value: "week" },
  { label: "Monthly", value: "month" },
  { label: "Custom", value: "custom" },
]

function periodTitle(kind) {
  if (kind === "day") return "Daily report"
  if (kind === "week") return "Weekly report"
  if (kind === "month") return "Monthly report"
  if (kind === "custom") return "Custom range report"
  return "Activity report"
}

function describePeriod(kind, start, end) {
  const from = start ? dayjs(start) : null
  const to = end ? dayjs(end) : null
  if (!from?.isValid()) return "Selected period"
  if (to?.isValid()) {
    const sameDay = from.isSame(to, "day")
    if (kind === "day" && sameDay) {
      return `From ${from.format("dddd, D MMM YYYY")} to ${to.format("dddd, D MMM YYYY")}`
    }
    return `From ${from.format("D MMM YYYY")} to ${to.format("D MMM YYYY")}`
  }
  if (kind === "month") return from.format("MMMM YYYY")
  return from.format("D MMM YYYY")
}

function unitFor(kind) {
  if (kind === "month") return "month"
  if (kind === "week") return "week"
  return "day"
}

function shiftPeriod(kind, date, delta) {
  return (date || dayjs()).add(delta, unitFor(kind))
}

function canGoNext(kind, date) {
  const next = shiftPeriod(kind, date, 1)
  const start =
    kind === "month"
      ? next.startOf("month")
      : kind === "week"
      ? next.startOf("isoWeek")
      : next.startOf("day")
  return !start.isAfter(dayjs(), "day")
}

function fileStamp(kind, start) {
  const value = dayjs(start || undefined)
  if (kind === "month") return value.format("YYYY-MM")
  if (kind === "week") {
    return `${value.isoWeekYear()}-W${String(value.isoWeek()).padStart(2, "0")}`
  }
  return value.format("YYYY-MM-DD")
}

function clock(value) {
  return value ? dayjs(value).format("YYYY-MM-DD HH:mm") : ""
}

function clockTime(value) {
  return value ? dayjs(value).format("HH:mm") : ""
}

function sumField(rows, field) {
  return rows.reduce((sum, row) => sum + Number(row?.[field] || 0), 0)
}

function rollupItems(rows, field) {
  const map = {}
  rows.forEach((row) => {
    ;(row?.[field] || []).forEach((item) => {
      const key = item.key || item.label
      if (!key) return
      if (!map[key]) {
        map[key] = { key, label: item.label || key, seconds: 0 }
      }
      map[key].seconds += Number(item.seconds || 0)
    })
  })
  return Object.values(map).sort((a, b) => b.seconds - a.seconds)
}

function hoursValue(seconds) {
  return hoursFromSeconds(seconds)
}

function downloadWorkbook(filename, sheets) {
  const workbook = XLSX.utils.book_new()
  Object.entries(sheets).forEach(([name, rows]) => {
    const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{}])
    XLSX.utils.book_append_sheet(workbook, sheet, name.slice(0, 31))
  })
  XLSX.writeFile(workbook, filename)
}

function RankList({ title, items, totalSeconds, empty }) {
  if (!items?.length) {
    return (
      <div className="time-use__chart">
        <div className="time-use__label">{title}</div>
        <div className="user-activity__muted">{empty}</div>
      </div>
    )
  }
  const max = items[0]?.seconds || 1
  return (
    <div className="time-use__chart">
      <div className="time-use__label">{title}</div>
      <div className="time-use__ranks">
        {items.slice(0, 10).map((item) => (
          <div className="time-use__rank" key={item.key}>
            <Tooltip title={item.doing || item.label || item.key}>
              <div className="time-use__rank-name">{item.label || item.key}</div>
            </Tooltip>
            <div className="time-use__rank-track">
              <div
                className="time-use__rank-fill"
                style={{ width: `${Math.max(4, (item.seconds / max) * 100)}%` }}
              />
            </div>
            <div className="time-use__rank-meta">
              {formatHours(item.seconds)}
              <span>{formatPercent(item.seconds, totalSeconds)}</span>
            </div>
            {item.details?.length ? (
              <div className="time-use__rank-doing">
                {item.details.slice(0, 4).map((detail) => (
                  <div className="time-use__rank-doing-row" key={detail.key}>
                    <span>{detail.label || detail.key}</span>
                    <em>{formatHours(detail.seconds)}</em>
                  </div>
                ))}
                {item.details.length > 4 ? (
                  <div className="time-use__rank-doing-row">
                    <span>+{item.details.length - 4} more</span>
                  </div>
                ) : null}
              </div>
            ) : item.doing ? (
              <div className="time-use__rank-doing">{item.doing}</div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  )
}

function addPdfTable(doc, title, head, body, fill, rightCols = []) {
  let startY = (doc.lastAutoTable?.finalY || 32) + 10
  if (startY > 178) {
    doc.addPage()
    startY = 16
  }
  doc.setFontSize(11)
  doc.setTextColor(20)
  doc.text(title, 14, startY)
  const columnStyles = {}
  rightCols.forEach((index) => {
    columnStyles[index] = { halign: "right" }
  })
  autoTable(doc, {
    startY: startY + 4,
    head: [head],
    body,
    styles: { fontSize: 7.5, cellPadding: 1.6 },
    margin: { left: 14, right: 14, bottom: 14 },
    headStyles: { fillColor: fill, textColor: 255 },
    columnStyles,
  })
}

function exportHoursPdf({
  title,
  subtitle,
  filename,
  users,
  devices,
  daily,
  apps,
  meetings,
  categories,
  summary = {},
}) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" })
  const generated = dayjs().format("D MMM YYYY HH:mm")
  doc.setFontSize(16)
  doc.text(title, 14, 16)
  doc.setFontSize(10)
  doc.setTextColor(80)
  const subtitleLines = doc.splitTextToSize(subtitle, 269)
  doc.text(subtitleLines, 14, 23)
  let cursorY = 23 + subtitleLines.length * 5
  doc.text(`Generated ${generated}`, 14, cursorY)
  cursorY += 8
  doc.setTextColor(20)
  doc.setFontSize(11)
  doc.text("Executive summary", 14, cursorY)
  autoTable(doc, {
    startY: cursorY + 2,
    head: [
      [
        "From",
        "To",
        "Users",
        "Active working",
        "Idle (no work)",
        "Utilization",
        "Work days",
        "Target hours",
        "Vs target",
      ],
    ],
    body: [
      [
        summary.rangeFrom
          ? dayjs(summary.rangeFrom).format("D MMM YYYY")
          : "",
        summary.rangeTo ? dayjs(summary.rangeTo).format("D MMM YYYY") : "",
        summary.people ?? users.length,
        `${Number(summary.focusHours || 0).toFixed(2)}h`,
        `${Number(summary.idleHours || 0).toFixed(2)}h`,
        `${Number(summary.utilizationPct || 0).toFixed(1)}%`,
        summary.workDays ?? "",
        `${Number(summary.targetHours || 0).toFixed(1)}h`,
        `${Number(summary.targetPct || 0).toFixed(1)}%`,
      ],
    ],
    styles: { fontSize: 8, cellPadding: 2 },
    margin: { left: 14, right: 14 },
    headStyles: { fillColor: [15, 23, 42], textColor: 255 },
  })

  autoTable(doc, {
    startY: (doc.lastAutoTable?.finalY || cursorY) + 8,
    head: [
      [
        "User",
        "Devices",
        "Active working",
        "Avg / day",
        "Idle (no work)",
        "Util %",
        "Meetings",
        "Calls",
        "Apps",
        "Browsing",
        "Media",
        "Days",
        "First",
        "Last",
      ],
    ],
    body: users.map((row) => [
      row.sessionUser || "—",
      (row.hostnames || []).join(", ") || String(row.devices || 0),
      hoursValue(row.focusSeconds).toFixed(2),
      Number(
        row.avgFocusHoursPerWorkDay || row.avgFocusHoursPerDay || 0
      ).toFixed(2),
      hoursValue(row.idleSeconds).toFixed(2),
      Number(row.utilizationPct || 0).toFixed(1),
      hoursValue(row.meetingSeconds).toFixed(2),
      hoursValue(row.callSeconds).toFixed(2),
      hoursValue(row.appSeconds).toFixed(2),
      hoursValue(row.browsingSeconds).toFixed(2),
      hoursValue(row.mediaSeconds).toFixed(2),
      row.daysActive || 0,
      clock(row.firstActivity),
      clock(row.lastActivity),
    ]),
    styles: { fontSize: 7, cellPadding: 1.4 },
    margin: { left: 14, right: 14, bottom: 14 },
    headStyles: { fillColor: [30, 64, 175], textColor: 255 },
    columnStyles: {
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "right" },
      5: { halign: "right" },
      6: { halign: "right" },
      7: { halign: "right" },
      8: { halign: "right" },
      9: { halign: "right" },
      10: { halign: "right" },
      11: { halign: "right" },
    },
  })

  if (daily.length) {
    addPdfTable(
      doc,
      "Hours by day",
      [
        "User",
        "Date",
        "Active working",
        "Idle (no work)",
        "Meetings",
        "Calls",
        "Apps",
        "Browsing",
        "Media",
        "Top app",
        "First",
        "Last",
      ],
      daily.map((row) => [
        row.sessionUser || "—",
        row.date,
        hoursValue(row.focusSeconds).toFixed(2),
        hoursValue(row.idleSeconds).toFixed(2),
        hoursValue(row.meetingSeconds).toFixed(2),
        hoursValue(row.callSeconds).toFixed(2),
        hoursValue(row.appSeconds).toFixed(2),
        hoursValue(row.browsingSeconds).toFixed(2),
        hoursValue(row.mediaSeconds).toFixed(2),
        row.topApp || "",
        clockTime(row.firstActivity),
        clockTime(row.lastActivity),
      ]),
      [15, 118, 110],
      [2, 3, 4, 5, 6, 7, 8]
    )
  }

  if (devices.length) {
    addPdfTable(
      doc,
      "By device",
      [
        "User",
        "Device",
        "Active working",
        "Idle (no work)",
        "Meetings",
        "Calls",
        "Top app",
        "Top meeting",
        "First",
        "Last",
      ],
      devices.map((row) => [
        row.sessionUser || "—",
        row.hostname || "—",
        hoursValue(row.focusSeconds).toFixed(2),
        hoursValue(row.idleSeconds).toFixed(2),
        hoursValue(row.meetingSeconds).toFixed(2),
        hoursValue(row.callSeconds).toFixed(2),
        row.topApp || "",
        row.topMeeting || "",
        clock(row.firstActivity),
        clock(row.lastActivity),
      ]),
      [71, 85, 105],
      [2, 3, 4, 5]
    )
  }

  if (categories.length) {
    addPdfTable(
      doc,
      "How time was spent",
      ["Category", "Hours", "Share"],
      categories.map((item) => [
        item.label || item.key,
        hoursValue(item.seconds).toFixed(2),
        formatPercent(item.seconds, sumField(categories, "seconds")),
      ]),
      [109, 40, 217],
      [1, 2]
    )
  }

  if (apps.length) {
    addPdfTable(
      doc,
      "Top applications",
      ["Application", "What they were doing", "Hours", "Share"],
      apps.slice(0, 20).map((item) => [
        item.label || item.key,
        item.doing ||
          (item.details || [])
            .slice(0, 3)
            .map((detail) => detail.label || detail.key)
            .join(" · ") ||
          "—",
        hoursValue(item.seconds).toFixed(2),
        formatPercent(item.seconds, sumField(apps, "seconds")),
      ]),
      [14, 116, 144],
      [2, 3]
    )
  }

  if (meetings.length) {
    addPdfTable(
      doc,
      "Meetings and calls",
      ["Title", "Hours", "Share"],
      meetings.slice(0, 20).map((item) => [
        item.label || item.key,
        hoursValue(item.seconds).toFixed(2),
        formatPercent(item.seconds, sumField(meetings, "seconds")),
      ]),
      [180, 83, 9],
      [1, 2]
    )
  }

  const pageCount = doc.getNumberOfPages()
  const pageHeight = doc.internal.pageSize.getHeight()
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page)
    doc.setFontSize(8)
    doc.setTextColor(120)
    doc.text(
      `Xpanse activity report  ·  page ${page} of ${pageCount}`,
      14,
      pageHeight - 8
    )
  }

  doc.save(filename)
}

function userHourRow(row) {
  return {
    User: row.sessionUser,
    Devices: (row.hostnames || [row.hostname]).filter(Boolean).join(", "),
    "Active working": hoursValue(row.focusSeconds),
    "Avg active / work day": Number(
      row.avgFocusHoursPerWorkDay || row.avgFocusHoursPerDay || 0
    ),
    "Idle (no work)": hoursValue(row.idleSeconds),
    "Utilization %": Number(row.utilizationPct || 0),
    "Meeting hours": hoursValue(row.meetingSeconds),
    "Call hours": hoursValue(row.callSeconds),
    "App hours": hoursValue(row.appSeconds),
    "Office hours": hoursValue(row.officeSeconds),
    "Browsing hours": hoursValue(row.browsingSeconds),
    "Media hours": hoursValue(row.mediaSeconds),
    Days: row.daysActive || 0,
    "Top app": row.topApp || "",
    "Top meeting": row.topMeeting || "",
    First: clock(row.firstActivity),
    Last: clock(row.lastActivity),
  }
}

function ActivityReport({
  data,
  loading,
  period = "week",
  date,
  rangeFrom,
  rangeTo,
  opts = {},
  onPeriodChange,
  onDateChange,
  onRangeChange,
  onOptsChange,
  onOpenDay,
}) {
  const [selectedUsers, setSelectedUsers] = useState([])
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [schedule, setSchedule] = useState(null)
  const [scheduleSaving, setScheduleSaving] = useState(false)
  const people = data?.people || []
  const summary = data?.summary || {}
  const kind = period || data?.period || "week"
  const viewDate = date || dayjs()
  const apiFrom = data?.rangeFrom || summary.rangeFrom
  const apiTo = data?.rangeTo || summary.rangeTo
  const localStart =
    apiFrom
      ? dayjs(apiFrom)
      : kind === "custom"
      ? rangeFrom || dayjs().startOf("month")
      : kind === "month"
      ? viewDate.startOf("month")
      : kind === "week"
      ? viewDate.startOf("isoWeek")
      : viewDate.startOf("day")
  const localEnd =
    apiTo
      ? dayjs(apiTo)
      : kind === "custom"
      ? rangeTo || dayjs()
      : kind === "month"
      ? viewDate.endOf("month")
      : kind === "week"
      ? viewDate.endOf("isoWeek")
      : viewDate.endOf("day")
  const rangeLabel = describePeriod(kind, localStart, localEnd)
  const allUsers = useMemo(() => {
    if (Array.isArray(data?.users) && data.users.length) return data.users
    return people
  }, [data?.users, people])

  const userOptions = useMemo(
    () =>
      allUsers
        .map((row) => ({
          value: String(row.sessionUser || "").toLowerCase(),
          label: row.sessionUser || "Unknown",
        }))
        .filter((item) => item.value),
    [allUsers]
  )

  const users = useMemo(() => {
    if (!selectedUsers.length) return allUsers
    const wanted = new Set(selectedUsers)
    return allUsers.filter((row) =>
      wanted.has(String(row.sessionUser || "").toLowerCase())
    )
  }, [allUsers, selectedUsers])

  const devices = useMemo(() => {
    if (!selectedUsers.length) return people
    const wanted = new Set(selectedUsers)
    return people.filter((row) =>
      wanted.has(String(row.sessionUser || "").toLowerCase())
    )
  }, [people, selectedUsers])

  const dailyRows = useMemo(
    () =>
      users.flatMap((user) =>
        (user.days || []).map((day) => ({
          ...day,
          sessionUser: user.sessionUser,
          hostname: (user.hostnames || [user.hostname])[0],
        }))
      ),
    [users]
  )

  const apps = useMemo(() => rollupItems(users, "apps"), [users])
  const meetings = useMemo(() => rollupItems(users, "meetings"), [users])
  const categories = useMemo(() => rollupItems(users, "categories"), [users])
  const totalTracked = sumField(users, "totalSeconds") || sumField(categories, "seconds")
  const dailySeries = useMemo(() => {
    if (Array.isArray(data?.dailySeries) && data.dailySeries.length) {
      return data.dailySeries
    }
    const map = {}
    dailyRows.forEach((row) => {
      if (!row.date) return
      if (!map[row.date]) {
        map[row.date] = { date: row.date, focusHours: 0, idleHours: 0 }
      }
      map[row.date].focusHours += hoursValue(row.focusSeconds)
      map[row.date].idleHours += hoursValue(row.idleSeconds)
    })
    return Object.values(map).sort((a, b) => String(a.date).localeCompare(String(b.date)))
  }, [data?.dailySeries, dailyRows])

  const groupOptions = useMemo(
    () =>
      (data?.groups || []).map((value) => ({ value, label: value })),
    [data?.groups]
  )
  const locationOptions = useMemo(
    () =>
      (data?.locations || []).map((value) => ({ value, label: value })),
    [data?.locations]
  )

  useEffect(() => {
    if (!scheduleOpen) return
    apiClient("settings/activity-report-schedule", "GET")
      .then((res) => {
        if (!res?.result) return
        setSchedule({
          ...res.result,
          recipientsText: (res.result.recipients || []).join("\n"),
        })
      })
      .catch(() => {})
  }, [scheduleOpen])

  const saveSchedule = async () => {
    if (!schedule) return
    setScheduleSaving(true)
    try {
      const res = await apiClient("settings/activity-report-schedule", "PUT", {
        ...schedule,
        recipients: String(schedule.recipientsText || "")
          .split(/[\n,;]+/)
          .map((item) => item.trim())
          .filter(Boolean),
      })
      if (res?.success) {
        setSchedule({
          ...res.result,
          recipientsText: (res.result.recipients || []).join("\n"),
        })
        message.success(res.message || "Schedule saved")
        setScheduleOpen(false)
      } else {
        message.error(res?.message || "Could not save schedule")
      }
    } catch (err) {
      message.error(err.message || "Could not save schedule")
    } finally {
      setScheduleSaving(false)
    }
  }

  const exportPayload = () => {
    const stamp = fileStamp(kind === "custom" ? "day" : kind, localStart)
    const who =
      users.length === 1
        ? String(users[0].sessionUser || "user").replace(/[^\w.-]+/g, "_")
        : selectedUsers.length
        ? `${users.length}-users`
        : "all-users"
    const title =
      users.length === 1
        ? `${periodTitle(kind)} — ${users[0].sessionUser}`
        : periodTitle(kind)
    const subtitle = `${rangeLabel}  ·  ${users.length} user${
      users.length === 1 ? "" : "s"
    }  ·  Active working vs idle (no work). Utilization ${
      summary.utilizationPct ?? 0
    }%  ·  Target ${summary.targetHoursPerDay || 8}h/work day.`
    const fileKind =
      { day: "daily", week: "weekly", month: "monthly", custom: "custom" }[
        kind
      ] || "activity"
    return { stamp, who, title, subtitle, fileKind }
  }

  const exportExcel = () => {
    const { stamp, who, fileKind } = exportPayload()
    downloadWorkbook(`${fileKind}-report-${who}-${stamp}.xlsx`, {
      Summary: [
        {
          Report: periodTitle(kind),
          From: localStart?.format?.("D MMM YYYY") || "",
          To: localEnd?.format?.("D MMM YYYY") || "",
          Period: rangeLabel,
          Users: users.length,
          Devices: new Set(devices.map((row) => row.hostname)).size,
          "Active working": hoursValue(sumField(users, "focusSeconds")),
          "Idle (no work)": hoursValue(sumField(users, "idleSeconds")),
          "Utilization %": summary.utilizationPct ?? 0,
          "Work days": summary.workDays ?? "",
          "Target hours": summary.targetHours ?? "",
          "Vs target %": summary.targetPct ?? "",
          "Meeting hours": hoursValue(sumField(users, "meetingSeconds")),
          "Call hours": hoursValue(sumField(users, "callSeconds")),
          "App hours": hoursValue(sumField(users, "appSeconds")),
          "Office hours": hoursValue(sumField(users, "officeSeconds")),
          "Browsing hours": hoursValue(sumField(users, "browsingSeconds")),
          "Media hours": hoursValue(sumField(users, "mediaSeconds")),
          "Tracked hours": hoursValue(totalTracked),
        },
      ],
      Users: users.map(userHourRow),
      Daily: dailyRows.map((row) => ({
        User: row.sessionUser,
        Date: row.date,
        "Active working": hoursValue(row.focusSeconds),
        "Idle (no work)": hoursValue(row.idleSeconds),
        "Meeting hours": hoursValue(row.meetingSeconds),
        "Call hours": hoursValue(row.callSeconds),
        "App hours": hoursValue(row.appSeconds),
        "Office hours": hoursValue(row.officeSeconds),
        "Browsing hours": hoursValue(row.browsingSeconds),
        "Media hours": hoursValue(row.mediaSeconds),
        "Top app": row.topApp || "",
        First: clock(row.firstActivity),
        Last: clock(row.lastActivity),
      })),
      Devices: devices.map((row) => ({
        User: row.sessionUser,
        Device: row.hostname,
        "Active working": hoursValue(row.focusSeconds),
        "Idle (no work)": hoursValue(row.idleSeconds),
        "Meeting hours": hoursValue(row.meetingSeconds),
        "Call hours": hoursValue(row.callSeconds),
        "App hours": hoursValue(row.appSeconds),
        "Browsing hours": hoursValue(row.browsingSeconds),
        "Media hours": hoursValue(row.mediaSeconds),
        "Top app": row.topApp || "",
        "Top meeting": row.topMeeting || "",
        Days: row.daysActive || 0,
        First: clock(row.firstActivity),
        Last: clock(row.lastActivity),
      })),
      Apps: users.flatMap((user) =>
        (user.apps || []).flatMap((item) => {
          const details = item.details || []
          if (!details.length) {
            return [
              {
                User: user.sessionUser,
                Application: item.label || item.key,
                Doing: item.doing || "",
                Hours: hoursValue(item.seconds),
                Share: formatPercent(
                  item.seconds,
                  user.totalSeconds || user.focusSeconds
                ),
              },
            ]
          }
          return details.map((detail) => ({
            User: user.sessionUser,
            Application: item.label || item.key,
            Doing: detail.label || detail.key,
            Hours: hoursValue(detail.seconds),
            Share: formatPercent(
              detail.seconds,
              user.totalSeconds || user.focusSeconds
            ),
          }))
        })
      ),
      Meetings: users.flatMap((user) =>
        (user.meetings || []).map((item) => ({
          User: user.sessionUser,
          Title: item.label || item.key,
          Hours: hoursValue(item.seconds),
        }))
      ),
      Categories: users.flatMap((user) =>
        (user.categories || []).map((item) => ({
          User: user.sessionUser,
          Category: item.label || item.key,
          Hours: hoursValue(item.seconds),
          Share: formatPercent(item.seconds, user.totalSeconds || user.focusSeconds),
        }))
      ),
    })
  }

  const exportPdf = () => {
    const { stamp, who, title, subtitle, fileKind } = exportPayload()
    exportHoursPdf({
      title,
      subtitle,
      filename: `${fileKind}-report-${who}-${stamp}.pdf`,
      users,
      devices,
      daily: dailyRows,
      apps,
      meetings,
      categories,
      summary: {
        ...summary,
        rangeFrom: summary.rangeFrom || localStart?.toISOString?.() || localStart,
        rangeTo: summary.rangeTo || localEnd?.toISOString?.() || localEnd,
        people: summary.people ?? users.length,
        focusHours:
          summary.focusHours ?? hoursValue(sumField(users, "focusSeconds")),
        idleHours:
          summary.idleHours ?? hoursValue(sumField(users, "idleSeconds")),
      },
    })
  }

  const rangePickerValue =
    kind === "custom" || kind === "month"
      ? [rangeFrom || localStart, rangeTo || localEnd]
      : [localStart, localEnd]

  const onRangePick = (values) => {
    const from = values?.[0] || dayjs().startOf("day")
    const to = values?.[1] || dayjs()
    onRangeChange?.(from.startOf("day"), to.endOf("day"))
    if (kind === "day") {
      if (from.isSame(to, "day")) {
        onDateChange?.(from)
      } else {
        onPeriodChange?.("custom")
      }
      return
    }
    if (kind === "week") {
      const weekStart = from.startOf("isoWeek")
      const weekEnd = from.endOf("isoWeek")
      if (from.isSame(weekStart, "day") && to.isSame(weekEnd, "day")) {
        onDateChange?.(from)
      } else {
        onPeriodChange?.("custom")
      }
    }
  }

  const toolbar = (
    <div className="time-use__header">
      <div>
        <div className="time-use__host">{periodTitle(kind)}</div>
        <div className="user-activity__muted">
          {rangeLabel}. Active working is time in apps, meetings, and calls.
          Idle (no work) is AFK time. Utilization vs tracked time; averages use
          work days when weekends/holidays are excluded.
        </div>
      </div>
      <div className="time-use__nav">
        <Segmented
          value={kind}
          options={PERIOD_OPTIONS}
          onChange={onPeriodChange}
        />
        {kind === "day" || kind === "week" ? (
          <>
            <Button
              icon={<LeftOutlined />}
              onClick={() => onDateChange?.(shiftPeriod(kind, viewDate, -1))}
            />
            <DatePicker
              picker={kind === "week" ? "week" : "date"}
              value={viewDate}
              allowClear={false}
              format={kind === "week" ? "[Week] w, D MMM" : "ddd, D MMM YYYY"}
              onChange={(value) => onDateChange?.(value || dayjs())}
              disabledDate={(current) =>
                current ? current.isAfter(dayjs(), "day") : false
              }
              style={{ width: 168 }}
            />
            <Button
              icon={<RightOutlined />}
              disabled={!canGoNext(kind, viewDate)}
              onClick={() => onDateChange?.(shiftPeriod(kind, viewDate, 1))}
            />
          </>
        ) : null}
        <RangePicker
          value={rangePickerValue}
          allowClear={false}
          format="D MMM YYYY"
          onChange={onRangePick}
          disabledDate={(current) =>
            current ? current.isAfter(dayjs(), "day") : false
          }
          style={{ width: 260 }}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Group"
          value={opts.group || undefined}
          options={groupOptions}
          onChange={(value) => onOptsChange?.({ group: value || "" })}
          style={{ minWidth: 120 }}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Location"
          value={opts.location || undefined}
          options={locationOptions}
          onChange={(value) => onOptsChange?.({ location: value || "" })}
          style={{ minWidth: 120 }}
        />
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="All users"
          value={selectedUsers}
          options={userOptions}
          onChange={setSelectedUsers}
          maxTagCount="responsive"
          style={{ minWidth: 180, maxWidth: 300 }}
        />
        <Tooltip title="Exclude weekends from work-day averages">
          <span className="time-use__toggle">
            <Switch
              size="small"
              checked={opts.excludeWeekends !== false}
              onChange={(checked) =>
                onOptsChange?.({ excludeWeekends: checked })
              }
            />{" "}
            Weekends
          </span>
        </Tooltip>
        <Tooltip title="Exclude holidays from work-day averages">
          <span className="time-use__toggle">
            <Switch
              size="small"
              checked={opts.excludeHolidays !== false}
              onChange={(checked) =>
                onOptsChange?.({ excludeHolidays: checked })
              }
            />{" "}
            Holidays
          </span>
        </Tooltip>
        <Button icon={<MailOutlined />} onClick={() => setScheduleOpen(true)}>
          Schedule
        </Button>
        <Button
          icon={<FilePdfOutlined />}
          disabled={!users.length}
          onClick={exportPdf}
        >
          PDF
        </Button>
        <Button
          icon={<DownloadOutlined />}
          disabled={!users.length}
          onClick={exportExcel}
        >
          Excel
        </Button>
      </div>
    </div>
  )

  if (loading && !data) {
    return (
      <div className="time-use">
        {toolbar}
        <div className="user-activity__muted">Loading activity report…</div>
      </div>
    )
  }

  if (!people.length) {
    return (
      <div className="time-use">
        {toolbar}
        <EmptyState
          title={`No activity for this ${
            kind === "day" ? "day" : kind === "custom" ? "range" : kind
          }`}
          description="Pick another from–to range, day, week, or month. Hosted Windows agents record apps, meetings, and idle time. Older Python agents only contribute session totals."
        />
      </div>
    )
  }

  return (
    <div className="time-use">
      {toolbar}

      <div className="user-activity__kpis">
        {[
          { label: "Users", value: users.length },
          {
            label: "From → To",
            value: `${localStart.format("D MMM")} → ${localEnd.format("D MMM")}`,
          },
          {
            label: "Active working",
            value: formatHours(
              summary.focusSeconds ?? sumField(users, "focusSeconds")
            ),
          },
          {
            label: "Idle (no work)",
            value: formatHours(
              summary.idleSeconds ?? sumField(users, "idleSeconds")
            ),
          },
          {
            label: "Utilization",
            value: `${Number(summary.utilizationPct || 0).toFixed(1)}%`,
          },
          {
            label: "Avg active / work day",
            value: `${Number(
              summary.avgFocusHoursPerWorkDay ||
                sumField(users, "avgFocusHoursPerWorkDay") /
                  Math.max(1, users.length) ||
                0
            ).toFixed(2)}h`,
          },
          {
            label: "Work days",
            value: summary.workDays ?? "—",
          },
          {
            label: "Vs target",
            value: `${Number(summary.targetPct || 0).toFixed(1)}%`,
          },
          {
            label: "Meetings",
            value: formatHours(sumField(users, "meetingSeconds")),
          },
          { label: "Calls", value: formatHours(sumField(users, "callSeconds")) },
        ].map((item) => (
          <div
            className="user-activity__kpi user-activity__kpi--static"
            key={item.label}
          >
            <div>
              <span className="user-activity__kpi-label">{item.label}</span>
              <strong>{item.value ?? "—"}</strong>
            </div>
          </div>
        ))}
      </div>

      {dailySeries.length > 1 ? (
        <div className="time-use__section time-use__trend">
          <div className="time-use__section-title">
            Daily active vs idle — {rangeLabel}
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={dailySeries}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => dayjs(value).format("D MMM")}
                minTickGap={24}
              />
              <YAxis
                tickFormatter={(value) => `${Number(value).toFixed(0)}h`}
                width={40}
              />
              <RechartTooltip
                formatter={(value, name) => [
                  `${Number(value).toFixed(2)}h`,
                  name === "focusHours" ? "Active working" : "Idle (no work)",
                ]}
                labelFormatter={(value) => dayjs(value).format("ddd D MMM YYYY")}
              />
              <Legend
                formatter={(value) =>
                  value === "focusHours" ? "Active working" : "Idle (no work)"
                }
              />
              <Bar
                dataKey="focusHours"
                name="focusHours"
                stackId="a"
                fill="#1d4ed8"
                radius={[2, 2, 0, 0]}
              />
              <Bar
                dataKey="idleHours"
                name="idleHours"
                stackId="a"
                fill="#94a3b8"
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      <Table
        className="time-use__devices"
        rowKey={(row) => String(row.sessionUser || "").toLowerCase()}
        size="small"
        pagination={{ pageSize: 12, showSizeChanger: false }}
        dataSource={users}
        scroll={{ x: 1380 }}
        expandable={{
          expandedRowRender: (row) => (
            <div className="time-use__nested">
              <Table
                size="small"
                pagination={false}
                rowKey={(item) => item.key}
                dataSource={row.apps || []}
                columns={[
                  {
                    title: "Application",
                    dataIndex: "key",
                    render: (value, item) => (
                      <span>
                        <span className="users-name">{value}</span>
                        {item.details?.length ? (
                          <div className="time-use__app-doing">
                            {item.details.slice(0, 6).map((detail) => (
                              <div key={detail.key}>
                                <span>{detail.label || detail.key}</span>
                                <em>{formatDuration(detail.seconds)}</em>
                              </div>
                            ))}
                          </div>
                        ) : item.doing ? (
                          <div className="user-activity__signals">{item.doing}</div>
                        ) : null}
                      </span>
                    ),
                  },
                  {
                    title: "Hours",
                    dataIndex: "seconds",
                    width: 90,
                    render: formatHours,
                  },
                  {
                    title: "Share",
                    dataIndex: "seconds",
                    width: 72,
                    render: (value) =>
                      formatPercent(value, row.totalSeconds || row.focusSeconds),
                  },
                ]}
              />
              <Table
                size="small"
                pagination={false}
                rowKey={(item) => item.key}
                dataSource={row.meetings || []}
                columns={[
                  { title: "Meeting / call", dataIndex: "key", ellipsis: true },
                  {
                    title: "Hours",
                    dataIndex: "seconds",
                    width: 90,
                    render: formatHours,
                  },
                ]}
                locale={{ emptyText: "No meetings or calls" }}
              />
              <Table
                size="small"
                pagination={false}
                rowKey={(item) => item.key}
                dataSource={row.categories || []}
                columns={[
                  {
                    title: "Category",
                    dataIndex: "label",
                    render: (value, item) => value || item.key,
                  },
                  {
                    title: "Hours",
                    dataIndex: "seconds",
                    width: 90,
                    render: formatHours,
                  },
                ]}
              />
            </div>
          ),
        }}
        columns={[
          {
            title: "User",
            dataIndex: "sessionUser",
            fixed: "left",
            width: 180,
            render: (value, row) => (
              <span>
                <span className="users-name">{value || "—"}</span>
                <div className="user-activity__signals">
                  {(row.hostnames || [row.hostname]).filter(Boolean).join(" · ") ||
                    "—"}
                </div>
              </span>
            ),
          },
          {
            title: "Active working",
            dataIndex: "focusSeconds",
            sorter: (a, b) => a.focusSeconds - b.focusSeconds,
            defaultSortOrder: "descend",
            render: (value, row) => (
              <span>
                {formatHours(value)}
                <div className="user-activity__signals">
                  {row.daysActive
                    ? `${Number(
                        row.avgFocusHoursPerWorkDay ||
                          row.avgFocusHoursPerDay ||
                          0
                      ).toFixed(2)}h / work day`
                    : formatDuration(value)}
                </div>
              </span>
            ),
          },
          {
            title: "Idle (no work)",
            dataIndex: "idleSeconds",
            sorter: (a, b) => a.idleSeconds - b.idleSeconds,
            render: formatHours,
          },
          {
            title: "Util %",
            dataIndex: "utilizationPct",
            width: 80,
            sorter: (a, b) =>
              Number(a.utilizationPct || 0) - Number(b.utilizationPct || 0),
            render: (value) => `${Number(value || 0).toFixed(1)}%`,
          },
          {
            title: "Meetings",
            dataIndex: "meetingSeconds",
            render: formatHours,
          },
          {
            title: "Calls",
            dataIndex: "callSeconds",
            render: formatHours,
          },
          {
            title: "Apps",
            dataIndex: "appSeconds",
            render: formatHours,
          },
          {
            title: "Browsing",
            dataIndex: "browsingSeconds",
            render: formatHours,
          },
          {
            title: "Media",
            dataIndex: "mediaSeconds",
            render: formatHours,
          },
          {
            title: "Top app",
            dataIndex: "topApp",
            ellipsis: true,
            render: (value) => value || "—",
          },
          {
            title: "Days",
            dataIndex: "daysActive",
            width: 72,
            render: (value) => value || 0,
          },
          {
            title: "First / last",
            key: "range",
            width: 170,
            render: (_, row) => (
              <span>
                {clock(row.firstActivity) || "—"}
                <div className="user-activity__signals">
                  {clock(row.lastActivity) || ""}
                </div>
              </span>
            ),
          },
        ]}
      />

      <div className="time-use__section time-use__charts">
        <RankList
          title="How time was spent"
          items={categories}
          totalSeconds={totalTracked}
          empty="No category mix for this selection."
        />
        <RankList
          title="Applications — what they were doing"
          items={apps}
          totalSeconds={sumField(apps, "seconds")}
          empty="No application time in this period."
        />
        <RankList
          title="Meetings and calls"
          items={meetings}
          totalSeconds={sumField(meetings, "seconds")}
          empty="No meetings or calls in this period."
        />
      </div>

      {dailyRows.length ? (
        <div className="time-use__section">
          <div className="time-use__label">Hours by day</div>
          <Table
            size="small"
            pagination={{ pageSize: 14, showSizeChanger: false }}
            rowKey={(row) => `${row.sessionUser}-${row.date}`}
            dataSource={dailyRows}
            scroll={{ x: 1100 }}
            onRow={(record) => ({
              onClick: () => onOpenDay?.(record.hostname, record.date),
            })}
            columns={[
              {
                title: "User",
                dataIndex: "sessionUser",
                width: 140,
                render: (value) => value || "—",
              },
              { title: "Date", dataIndex: "date", width: 110 },
              {
                title: "Active working",
                dataIndex: "focusSeconds",
                render: formatHours,
              },
              {
                title: "Idle (no work)",
                dataIndex: "idleSeconds",
                render: formatHours,
              },
              {
                title: "Meetings",
                dataIndex: "meetingSeconds",
                render: formatHours,
              },
              {
                title: "Calls",
                dataIndex: "callSeconds",
                render: formatHours,
              },
              {
                title: "Browsing",
                dataIndex: "browsingSeconds",
                render: formatHours,
              },
              {
                title: "Media",
                dataIndex: "mediaSeconds",
                render: formatHours,
              },
              {
                title: "Top app",
                dataIndex: "topApp",
                ellipsis: true,
                render: (value) => value || "—",
              },
              {
                title: "First / last",
                key: "window",
                width: 110,
                render: (_, row) =>
                  row.firstActivity
                    ? `${clockTime(row.firstActivity)}–${clockTime(row.lastActivity)}`
                    : "—",
              },
            ]}
          />
        </div>
      ) : null}

      {devices.length ? (
        <div className="time-use__section">
          <div className="time-use__label">By device</div>
          <Table
            size="small"
            pagination={{ pageSize: 8, showSizeChanger: false }}
            rowKey={(row) => `${row.sessionUser}-${row.hostname}`}
            dataSource={devices}
            onRow={(record) => ({
              onClick: () => onOpenDay?.(record.hostname, record.lastActivity),
            })}
            columns={[
              {
                title: "User",
                dataIndex: "sessionUser",
                render: (value) => value || "—",
              },
              { title: "Device", dataIndex: "hostname", render: (v) => v || "—" },
              {
                title: "Active working",
                dataIndex: "focusSeconds",
                render: formatHours,
              },
              {
                title: "Idle (no work)",
                dataIndex: "idleSeconds",
                render: formatHours,
              },
              {
                title: "Meetings",
                dataIndex: "meetingSeconds",
                render: formatHours,
              },
              {
                title: "Top app",
                dataIndex: "topApp",
                ellipsis: true,
                render: (value) => value || "—",
              },
              {
                title: "Top meeting",
                dataIndex: "topMeeting",
                ellipsis: true,
                render: (value) => value || "—",
              },
            ]}
          />
        </div>
      ) : null}

      <div className="time-use__hint">
        Expand a user for application and meeting detail. Click a day or device
        row for that timeline. {rangeLabel}.
        {summary.spanCount ? ` ${summary.spanCount} activity spans.` : ""}
      </div>

      <Drawer
        title="Email work-hours reports"
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        width={420}
        destroyOnClose
        extra={
          <Button
            type="primary"
            loading={scheduleSaving}
            disabled={!schedule}
            onClick={saveSchedule}
          >
            Save
          </Button>
        }
      >
        {schedule ? (
          <Form layout="vertical">
            <Form.Item label="Enable scheduled email">
              <Switch
                checked={Boolean(schedule.enabled)}
                onChange={(checked) =>
                  setSchedule((prev) => ({ ...prev, enabled: checked }))
                }
              />
            </Form.Item>
            <Form.Item label="Report period">
              <Segmented
                value={schedule.period || "week"}
                options={[
                  { label: "Daily", value: "day" },
                  { label: "Weekly", value: "week" },
                  { label: "Monthly", value: "month" },
                ]}
                onChange={(value) =>
                  setSchedule((prev) => ({ ...prev, period: value }))
                }
              />
              <div className="user-activity__muted" style={{ marginTop: 8 }}>
                Daily every morning · Weekly Mondays · Monthly on the 1st.
                Each mail includes From and To dates for the period.
              </div>
            </Form.Item>
            <Form.Item label="Recipients (one email per line)">
              <Input.TextArea
                rows={5}
                value={schedule.recipientsText || ""}
                onChange={(event) =>
                  setSchedule((prev) => ({
                    ...prev,
                    recipientsText: event.target.value,
                  }))
                }
                placeholder="manager@company.com"
              />
            </Form.Item>
            <Form.Item label="Target hours / work day">
              <InputNumber
                min={1}
                max={24}
                value={schedule.targetHoursPerDay || 8}
                onChange={(value) =>
                  setSchedule((prev) => ({
                    ...prev,
                    targetHoursPerDay: value || 8,
                  }))
                }
              />
            </Form.Item>
            <Form.Item label="Group filter (optional)">
              <Input
                value={schedule.group || ""}
                onChange={(event) =>
                  setSchedule((prev) => ({
                    ...prev,
                    group: event.target.value,
                  }))
                }
                placeholder="Leave blank for all"
              />
            </Form.Item>
            <Form.Item label="Exclude weekends">
              <Switch
                checked={schedule.excludeWeekends !== false}
                onChange={(checked) =>
                  setSchedule((prev) => ({
                    ...prev,
                    excludeWeekends: checked,
                  }))
                }
              />
            </Form.Item>
            <Form.Item label="Exclude holidays">
              <Switch
                checked={schedule.excludeHolidays !== false}
                onChange={(checked) =>
                  setSchedule((prev) => ({
                    ...prev,
                    excludeHolidays: checked,
                  }))
                }
              />
            </Form.Item>
            {schedule.lastSentAt ? (
              <div className="user-activity__muted">
                Last sent {dayjs(schedule.lastSentAt).format("D MMM YYYY HH:mm")}
                {schedule.lastError ? ` · ${schedule.lastError}` : ""}
              </div>
            ) : null}
          </Form>
        ) : (
          <div className="user-activity__muted">Loading schedule…</div>
        )}
      </Drawer>
    </div>
  )
}

export default ActivityReport
